import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  Grid,
  IconButton,
  DialogActions,
  Autocomplete,
  TextField,
  Box,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemText,
  CardHeader,
  CardContent,
} from "../../../../components";
import { AtivoServicoService, notification } from "../../../../services";
import shortid from "shortid";
import { InputErros } from "../../../../helpers";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

const tiposCampo = [
  { id: 1, type: "date", descricao: "Data", icon: "ph-calendar" },
  { id: 2, type: "number", descricao: "Número", icon: "ph-number-square-one" },
  { id: 3, type: "string", descricao: "Texto", icon: "ph-text-t" },
  { id: 4, type: "money", descricao: "Monetário", icon: "ph-currency-dollar" },
];

const ativoServicoService = new AtivoServicoService();

const CampoPersonalizadoItem = ({ campo, onUpdate, onDelete, onEdit, provided }) => {
  const getTipoIcon = (type) => {
    const tipo = tiposCampo.find((t) => t.type === type);
    return tipo ? tipo.icon : type;
  };

  const getTipoDescricao = (type) => {
    const tipo = tiposCampo.find((t) => t.type === type);
    return tipo ? tipo.descricao : type;
  };

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={{ width: "100%", ...provided.draggableProps.style }}
    >
      <Grid
        container
        item
        xs={12}
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          mb: 1.5,
          ml: 1,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fff",
        }}
      >
        <Grid
          item
          xs={1}
          sm={0.5}
          {...provided.dragHandleProps}
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
        >
          <i
            className="ph-bold ph-dots-six-vertical"
            style={{ fontSize: 20, color: "#555" }}
          ></i>
        </Grid>

        <Grid item xs={11} sm={3.5}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem" }}
          >
            Nome do campo:
          </Typography>
          <Typography
            variant="body1"
            fontWeight={500}
            sx={{ fontSize: "0.95rem" }}
          >
            {campo.label}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <i
              className={`ph-fill ${getTipoIcon(campo.type)}`}
              style={{ fontSize: 22, color: "#757575" }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              {getTipoDescricao(campo.type)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Switch
                checked={campo.obrigatoriedade || false}
                onChange={(e) =>
                  onUpdate({ ...campo, obrigatoriedade: e.target.checked })
                }
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: "0.875rem", ml: 0.5 }}>
                {campo.obrigatoriedade ? "Obrigatório" : "Opcional"}
              </Typography>
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={2}
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          <Tooltip title="Editar campo">
            <Button
              variant="contained"
              size="small"
              onClick={() => onEdit(campo)}
              sx={{
                height: "35px",
                borderRadius: "50%",
                minWidth: "15px",
                boxShadow: "none",
                backgroundColor: "#EDEEF0",
                "&:hover": { backgroundColor: "#B6BABC" },
              }}
            >
              <i className="ph-fill ph-pencil" style={{ fontSize: 15 }}></i>
            </Button>
          </Tooltip>
          <Tooltip title="Remover campo">
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => onDelete(campo.id)}
              sx={{
                height: "35px",
                borderRadius: "50%",
                minWidth: "15px",
                boxShadow: "none",
              }}
            >
              <i
                className="ph-bold ph-trash"
                style={{ color: "#fff", fontSize: 15 }}
              ></i>
            </Button>
          </Tooltip>
        </Grid>
      </Grid>
    </div>
  );
};

const ListaAtivos = ({ ativos, onEditAtivo, onDeleteAtivo }) => {
  if (ativos.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="textSecondary">Nenhum ativo cadastrado</Typography>
      </Box>
    );
  }

  return (
    <List>
      {ativos.map((ativo) => (
        <ListItem
          key={ativo.id}
          divider
          sx={{
            borderRadius: "8px",
            "&:hover": { backgroundColor: "#f5f5f5" },
            border: "1px solid #e5e5e5",
          }}
        >
          <ListItemText
            primary={ativo.nome}
            secondary={
              <Box component="span">
                {ativo.ativado ? "Ativado" : "Desativado"} •{" "}
                {ativo.camposPersonalizados?.length || 0} campos
              </Box>
            }
          />
          <>
            <Tooltip title="Editar">
              <IconButton
                edge="end"
                onClick={() => onEditAtivo(ativo)}
                size="small"
                sx={{ mr: 1 }}
              >
                <i className="ph-fill ph-pencil-simple"></i>
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton
                edge="end"
                onClick={() => onDeleteAtivo(ativo.id)}
                size="small"
              >
                <i className="ph-fill ph-trash"></i>
              </IconButton>
            </Tooltip>
          </>
        </ListItem>
      ))}
    </List>
  );
};

export function CamposPersonalizadosAtivosDialog({
  open,
  setOpen,
  ativosExistentes = [],
}) {
  const [tabAtiva, setTabAtiva] = useState(0);
  const [ativos, setAtivos] = useState(ativosExistentes);
  const [ativoEditando, setAtivoEditando] = useState(null);
  const [novoAtivo, setNovoAtivo] = useState({
    nome: "",
    ativado: true,
    camposPersonalizados: [],
  });
  const [novoCampo, setNovoCampo] = useState({
    tipo: null,
    label: "",
  });
  const [campoEditandoId, setCampoEditandoId] = useState(null);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    if (open) {
      buscarAtivos();
    }
  }, [open]);

  const buscarAtivos = async () => {
    const response = await ativoServicoService.getAll();
    if (!response.isAxiosError) {
      setAtivos(response.data);
    }
  };

  const onClose = () => {
    setOpen(false);
    setTabAtiva(0);
    resetFormulario();
  };

  const resetFormulario = () => {
    setAtivoEditando(null);
    setNovoAtivo({
      nome: "",
      ativado: true,
      camposPersonalizados: [],
    });
    setNovoCampo({
      tipo: null,
      label: "",
    });
    setCampoEditandoId(null);
  };

  const iniciarEdicaoCampo = (campo) => {
    const tipoCorrespondente = tiposCampo.find((t) => t.type === campo.type);
    setNovoCampo({
      tipo: tipoCorrespondente || null,
      label: campo.label,
    });
    setCampoEditandoId(campo.id);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const ativoAtual = ativoEditando || novoAtivo;
    const items = Array.from(ativoAtual.camposPersonalizados);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    if (ativoEditando) {
      setAtivoEditando({ ...ativoEditando, camposPersonalizados: items });
    } else {
      setNovoAtivo({ ...novoAtivo, camposPersonalizados: items });
    }
  };

  const handleAdicionarCampoPersonalizado = () => {
    if (!novoCampo.label?.trim()) {
      notification.erroValidacao("Nome do Campo");
      setInputErro("nomeCampo");
      return;
    }
    if (!novoCampo.tipo) {
      notification.erroValidacao("Tipo do Campo");
      setInputErro("tipoCampo");
      return;
    }

    const ativoAtual = ativoEditando || novoAtivo;

    if (campoEditandoId) {
      const campoAtualizado = {
        id: campoEditandoId,
        label: novoCampo.label.trim(),
        type: novoCampo.tipo.type,
        obrigatoriedade:
          ativoAtual.camposPersonalizados.find((c) => c.id === campoEditandoId)
            ?.obrigatoriedade || false,
      };

      if (ativoEditando) {
        setAtivoEditando((prev) => ({
          ...prev,
          camposPersonalizados: prev.camposPersonalizados.map((campo) =>
            campo.id === campoEditandoId ? campoAtualizado : campo,
          ),
        }));
      } else {
        setNovoAtivo((prev) => ({
          ...prev,
          camposPersonalizados: prev.camposPersonalizados.map((campo) =>
            campo.id === campoEditandoId ? campoAtualizado : campo,
          ),
        }));
      }
      setCampoEditandoId(null);
    } else {
      const novoCampoCompleto = {
        id: shortid.generate(),
        label: novoCampo.label.trim(),
        type: novoCampo.tipo.type,
        obrigatoriedade: false,
      };

      if (ativoEditando) {
        setAtivoEditando((prev) => ({
          ...prev,
          camposPersonalizados: [
            ...prev.camposPersonalizados,
            novoCampoCompleto,
          ],
        }));
      } else {
        setNovoAtivo((prev) => ({
          ...prev,
          camposPersonalizados: [
            ...prev.camposPersonalizados,
            novoCampoCompleto,
          ],
        }));
      }
    }

    inputErros.limpar();
    setNovoCampo({ tipo: null, label: "" });
  };

  const handleRemoverCampoPersonalizado = (idCampo) => {
    if (ativoEditando) {
      setAtivoEditando((prev) => ({
        ...prev,
        camposPersonalizados: prev.camposPersonalizados.filter(
          (campo) => campo.id !== idCampo,
        ),
      }));
      if (campoEditandoId === idCampo) {
        setCampoEditandoId(null);
        setNovoCampo({ tipo: null, label: "" });
      }
    } else {
      setNovoAtivo((prev) => ({
        ...prev,
        camposPersonalizados: prev.camposPersonalizados.filter(
          (campo) => campo.id !== idCampo,
        ),
      }));
      if (campoEditandoId === idCampo) {
        setCampoEditandoId(null);
        setNovoCampo({ tipo: null, label: "" });
      }
    }
  };

  const handleAtualizarCampoPersonalizado = (campoAtualizado) => {
    if (ativoEditando) {
      setAtivoEditando((prev) => ({
        ...prev,
        camposPersonalizados: prev.camposPersonalizados.map((campo) =>
          campo.id === campoAtualizado.id ? campoAtualizado : campo,
        ),
      }));
    } else {
      setNovoAtivo((prev) => ({
        ...prev,
        camposPersonalizados: prev.camposPersonalizados.map((campo) =>
          campo.id === campoAtualizado.id ? campoAtualizado : campo,
        ),
      }));
    }
  };

  const handleEditarAtivo = (ativo) => {
    const camposOrdenados = [...(ativo.camposPersonalizados || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    
    setAtivoEditando({ ...ativo, camposPersonalizados: camposOrdenados });
    setTabAtiva(1);
    setCampoEditandoId(null);
    setNovoCampo({ tipo: null, label: "" });
  };

  const handleExcluirAtivo = async (id) => {
    const confirmacaoAlerta = await notification.confirmacao(
      "Confirmação!",
      "Tem certeza que deseja excluir este ativo?",
    );
    if (confirmacaoAlerta.isConfirmed) {
      const ativosTemp = ativos.filter((ativo) => ativo.id !== id);
      setAtivos(ativosTemp);
      if (ativoEditando?.id === id) {
        resetFormulario();
        setTabAtiva(0);
      }
      const result = await ativoServicoService.deletar(id);
      if (!result.isAxiosError) {
        notification.deletadoSucesso();
      }
    }
  };

  const handleSalvarAtivo = async () => {
    const baseAtivo = ativoEditando || novoAtivo;
    const ativoParaSalvar = {
      ...baseAtivo,
      camposPersonalizados: baseAtivo.camposPersonalizados.map((campo, index) => ({
        ...campo,
        ordem: index + 1,
      })),
    };

    if (!ativoParaSalvar.nome.trim()) {
      notification.erroValidacao("Nome do Ativo");
      setInputErro("nomeAtivo");
      return;
    }

    if (!ativoParaSalvar.camposPersonalizados.length) {
      notification.alertaGenericos("Adicione ao menos um campo personalizado.");
      setInputErro(["tipoCampo", "nomeCampo"]);
      return;
    }

    if (ativoEditando) {
      const ativosTemp = [
        ...ativos.map((ativo) =>
          ativo.id === ativoEditando.id ? ativoParaSalvar : ativo,
        ),
      ];
      setAtivos(ativosTemp);
      const result = await ativoServicoService.atualizar(
        ativoEditando.id,
        ativoParaSalvar,
      );
      if (!result.isAxiosError) {
        notification.alteracaoSucesso();
      }
    } else {
      const novoAtivoComId = {
        ...ativoParaSalvar,
        id: shortid.generate(),
      };
      const ativosTemp = [...ativos, novoAtivoComId];
      setAtivos(ativosTemp);
      const result = await ativoServicoService.cadastrar(ativoParaSalvar);
      if (!result.isAxiosError) {
        notification.cadastroSucesso();
      }
    }
    resetFormulario();
    setTabAtiva(0);
  };

  const ativoAtual = ativoEditando || novoAtivo;
  const emModoEdicao = !!ativoEditando;
  const emEdicaoCampo = !!campoEditandoId;

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <CardContent>
        <CardHeader
          title="Configuração de Ativos de Serviço"
          action={
            <IconButton
              color="inherit"
              onClick={onClose}
              sx={{
                backgroundColor: "#EDEEF0",
                "&:hover": { backgroundColor: "#B6BABC" },
              }}
            >
              <i
                className="ph-bold ph-x"
                style={{ fontSize: "clamp(17px, 4vw, 20px)" }}
              ></i>
            </IconButton>
          }
        />
        <div className="m-3">
          {tabAtiva === 0 ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={9}>
                  <Typography variant="body2" gutterBottom>
                    Ativos Cadastrados
                  </Typography>
                </Grid>
                <Grid item xs={3} textAlign="right">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      resetFormulario();
                      setTabAtiva(1);
                    }}
                  >
                    Adicionar Ativo
                  </Button>
                </Grid>
              </Grid>
              <ListaAtivos
                ativos={ativos}
                onEditAtivo={handleEditarAtivo}
                onDeleteAtivo={handleExcluirAtivo}
              />
            </>
          ) : (
            <>
              <Grid container sx={{ mb: 2 }} alignItems="center">
                <Grid item xs={1}>
                  <Tooltip title="Voltar">
                    <IconButton
                      onClick={() => {
                        setTabAtiva(0);
                        setCampoEditandoId(null);
                        setNovoCampo({ tipo: null, label: "" });
                      }}
                      size="small"
                    >
                      <i
                        className="ph-fill ph-arrow-left"
                        style={{ fontSize: 20 }}
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="h6">
                    {emModoEdicao
                      ? `Editando Ativo - ${ativoEditando.nome}`
                      : "Cadastrar Novo Ativo"}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                    Informações do Ativo
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <TextField
                        id="nomeAtivo"
                        name="nomeAtivo"
                        label="Nome do Ativo"
                        fullWidth
                        required
                        value={ativoAtual.nome}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (emModoEdicao) {
                            setAtivoEditando({ ...ativoAtual, nome: value });
                          } else {
                            setNovoAtivo({ ...ativoAtual, nome: value });
                          }
                        }}
                        error={inputErros.get("nomeAtivo")}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ativoAtual.ativado}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (emModoEdicao) {
                                setAtivoEditando({
                                  ...ativoAtual,
                                  ativado: checked,
                                });
                              } else {
                                setNovoAtivo({
                                  ...ativoAtual,
                                  ativado: checked,
                                });
                              }
                            }}
                            color="primary"
                          />
                        }
                        label={ativoAtual.ativado ? "Ativado" : "Desativado"}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                    {emEdicaoCampo
                      ? "Editar Campo Personalizado"
                      : "Adicionar Campo Personalizado"}
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={5}>
                      <TextField
                        id="nomeCampo"
                        label="Nome do Campo"
                        fullWidth
                        value={novoCampo.label}
                        error={inputErros.get("nomeCampo")}
                        onChange={(e) =>
                          setNovoCampo({ ...novoCampo, label: e.target.value })
                        }
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            document.getElementById("buttonAdicionar")?.click();
                            document.getElementById("nomeCampo")?.blur();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <Autocomplete
                        options={tiposCampo}
                        getOptionLabel={(option) => option.descricao}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            id="tipoCampo"
                            label="Tipo do Campo"
                            error={inputErros.get("tipoCampo")}
                          />
                        )}
                        value={novoCampo.tipo}
                        onChange={(e, value) =>
                          setNovoCampo({ ...novoCampo, tipo: value })
                        }
                      />
                    </Grid>
                    <Grid item xs={2}>
                      {emEdicaoCampo ? (
                        <Button
                          id="buttonAdicionar"
                          variant="contained"
                          size="small"
                          sx={{
                            height: "35px",
                            borderRadius: "50%",
                            minWidth: "15px",
                            boxShadow: "none",
                          }}
                          onClick={handleAdicionarCampoPersonalizado}
                        >
                          <i
                            className="ph-bold ph-arrows-clockwise"
                            style={{ color: "#fff", fontSize: 15 }}
                          ></i>
                        </Button>
                      ) : (
                        <Button
                          id="buttonAdicionar"
                          variant="contained"
                          color="success"
                          size="small"
                          sx={{
                            height: "35px",
                            borderRadius: "50%",
                            minWidth: "15px",
                            boxShadow: "none",
                          }}
                          onClick={handleAdicionarCampoPersonalizado}
                        >
                          <i
                            className="ph-bold ph-plus"
                            style={{ color: "#fff", fontSize: 15 }}
                          ></i>
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                {ativoAtual.camposPersonalizados.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                      Campos Personalizados Configurados
                    </Typography>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="campos-personalizados">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{ width: "100%" }}
                          >
                            {ativoAtual.camposPersonalizados.map((campo, index) => (
                              <Draggable
                                key={campo.id}
                                draggableId={String(campo.id)}
                                index={index}
                              >
                                {(provided) => (
                                  <CampoPersonalizadoItem
                                    campo={campo}
                                    onUpdate={handleAtualizarCampoPersonalizado}
                                    onDelete={handleRemoverCampoPersonalizado}
                                    onEdit={iniciarEdicaoCampo}
                                    provided={provided}
                                  />
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </div>
      </CardContent>
      <DialogActions className="m-3">
        <Button onClick={onClose} variant="contained" color="padrao">
          Fechar
        </Button>
        {tabAtiva === 1 && (
          <Button
            onClick={handleSalvarAtivo}
            variant="contained"
            color="primary"
          >
            {emModoEdicao ? "Atualizar" : "Salvar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}